import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import logoImg from "../assets/csir_logo.png";
import heroVideo from "../assets/hero_video.mp4";
import thumbnail from "../assets/hero-thumbnail.jpeg";
import featureImage1 from "../assets/uploadEL.webp";
import featureImage2 from "../assets/view.webp";
import featureImage3 from "../assets/home_about.webp";
import demoVideo from "../assets/solar_aidd.mp4";

const LandingPage2 =()=>{
    const navigate = useNavigate();

    const [activeLink, setActiveLink] = useState('/');

    const [showModal, setShowModal] = useState(false);

    const features = [
        {
        image: featureImage1,
        title: "Upload EL images",
        description: "Upload batches of PV EL images to detect defects at batch, module, and cell levels",
        },
        {
        image: featureImage2,
        title: "View Analysis",
        description: "Analyze heatmaps of all uploaded batches with defective modules at batch, module and cell level.",
        },
        {
        image: featureImage3,
        title: "About SolarAIDD",
        description: "Learn more about our AI-powered solar PV defect detection platform and how it works.",
        },
    ];
    return (
        <PageContainer>
            <Helmet>
                {/* Standard Tags */}
                <html lang="en" />
                <title>SolarAIDD | CSIR </title>
                <meta name="description" content="Automate your PV inspection workflow. SolarAIDD uses deep learning to identify defects in EL images with cell-level precision. Developed by CSIR."/>
                <meta name="keywords" content="automated PV inspection, solar panel quality control, EL image processing, PV defect classification, solar energy AI" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://solaraidd.csir.co.za/"/>
                <script type="application/ld+json">
                  {`
                  {
                    "@context": "https://schema.org",
                    "@type": "VideoObject",
                    "name": "SolarAI Demo Video",
                    "description": "Overview of SolarAI analytics platform.",
                    "thumbnailUrl": "${thumbnail}",
                    "uploadDate": "2026-03-10T12:00:00Z",
                    "contentUrl": "${heroVideo}",
                    "embedUrl": "https://solaraidd.csir.co.za/#hero-video",
                    "duration": "PT2M15S"
                  }
                  `}
                </script>
            </Helmet>

            {/* Navigation Bar */}
            <Navbar>
                <BrandContainer onClick={() => navigate('/')}>
                    <Logo src={logoImg} alt="Logo" />
                    <Brand>SolarAIDD</Brand>
                </BrandContainer>
                <NavLinks>
                  <NavLink isActive ={activeLink ==='/login'} onClick={() =>{navigate('/login'); setActiveLink('/login')}}>Login</NavLink>
                  <NavLink isActive={activeLink ==='/contactUs'} onClick={() => navigate('/contactUs')}>Contact Us</NavLink>
                  <NeonButton as="a" href="https://outlook.office.com/book/solaraiddcsircoza@csir.co.za/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">Book A Demo</NeonButton>
                </NavLinks>
            </Navbar>

            <ModalOverlay visible={showModal}>
                <ModalContent>
                    <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
                    <h2>Request a Demo</h2>
                    <Form onSubmit={(e) => { e.preventDefault(); alert("Demo Requested!"); }}>
                        <Input type="text" placeholder="Your Name" required />
                        <Input type="email" placeholder="Your Email" required />
                        <TextArea placeholder="Tell us about your needs..." />
                        <SubmitButton type="submit">Submit Request</SubmitButton>
                    </Form>
                </ModalContent>
            </ModalOverlay>

            {/* Hero Section */}
            <HeroVideoSection>
              <VideoBackground autoPlay muted loop playsInline>
                <source src={heroVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </VideoBackground>
              
              <Overlay>  
                <HeroTextContainer>
                  <h1>
                    <span>Solar</span><br />
                    <span>Artificial Intelligence</span><br />
                    <span>Defect Detection</span>
                  </h1>
                  <p>Detect defects in solar PV EL images at batch, module, and cell levels with high accuracy.</p>
                  <HeroButtonsContainer>
                    <NeonButton onClick={() => navigate('/login')}>Get Started</NeonButton>
                  </HeroButtonsContainer>
                </HeroTextContainer>
              </Overlay>
            </HeroVideoSection>

            {/* How It Works Section */}
            <HowItWorksSection>
                <h2>How It Works</h2>
                <StepsContainer>
                <Step>
                    <StepNumber>1</StepNumber>
                    <StepDescription>Upload batches of PV EL images and configure settings.</StepDescription>
                </Step>
                <Step>
                    <StepNumber>2</StepNumber>
                    <StepDescription>Get insights into which modules have defects and where they are located</StepDescription>
                </Step>
                <Step>
                    <StepNumber>3</StepNumber>
                    <StepDescription>Generate reports to track and improve efficiency.</StepDescription>
                </Step>
                </StepsContainer>

                <DemoVideo>
                    <video controls>
                        <source src={demoVideo} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </DemoVideo>
            </HowItWorksSection>

            <FeaturesContainer>
                <h2>Our Features</h2>
                <CardGrid>
                    {features.map((feature, index) => (
                    <FeatureCard key={index}>
                        <FeatureImage src={feature.image} alt={feature.title} />
                        <FeatureTitle>{feature.title}</FeatureTitle>
                        <FeatureDescription>{feature.description}</FeatureDescription>
                    </FeatureCard>
                    ))}
                </CardGrid>
                </FeaturesContainer>
        </PageContainer>
  );
};

export default LandingPage2;

// Container for the entire page (Hero section + Login section)
const PageContainer = styled.div`
   display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden; /* Prevents layout shifting issues */
`;

// Navigation Bar Styling (Responsive)
const Navbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color:#293439; 
  color: white;
  z-index: 3; // Stay above all other elements
  box-shadow: 0 2px 10px rgba(22, 36, 66, 0);
`;

// Brand/Logo Text Styling
const BrandContainer = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const Brand = styled.div`
  font-size: 1.5rem;
  font-weight: bold;

  // Media query for smaller screens
  @media (max-width: 768px) {
    font-size: 1.2rem; // Smaller logo size for mobile
  }
;
`;

const Logo = styled.img`
    width: 133px; /* Adjust size as needed */
    height: 45px;
    margin-right: 0.5rem; /* Space between logo and brand text */
`;
  
// Navigation Links Container
const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: cyan;
  }

  &.active {
    color: cyan;
  }

  // Media query for smaller screens
  @media (max-width: 768px) {
    flex-direction: column; // Stack links vertically
    gap: 0.5rem; // Reduce spacing between links
    width: 100%;
    align-items: center; // Center the links on smaller screens
  }
`;
  
// Link Styling
const NavLink = styled.button`
    background: none;
    border: none;
    color: white;
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #E55B41;
    }

    // Media query for smaller screens
    @media (max-width: 768px) {
        font-size: 1rem; // Reduce font size on smaller screens
        padding: 0.5rem; // Adjust padding
    }
    ;
`;

const NeonButton = styled.button`
  padding: 0.6rem 1.4rem;
  font-size: 1rem;
  color: #0ff;
  background: transparent;
  border: 2px solid #0ff;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 0 5px #0ff, 0 0 10px #0ff;
  text-decoration: none;

  &:hover {
    background: #0ff;
    color: #000;
    box-shadow: 0 0 10px #0ff, 0 0 20px #0ff, 0 0 30px #0ff;
  }

  &::before {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    z-index: -1;
    background: linear-gradient(90deg, #0ff, #00f, #0ff);
    background-size: 400%;
    border-radius: 30px;
    animation: glowMove 4s linear infinite;
    opacity: 0.4;
  }

  @keyframes glowMove {
    0% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 400% 50%;
    }
  }
`;

// Overlay background
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

// Modal box
const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  border-radius: 10px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 14px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  padding: 0.7rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const TextArea = styled.textarea`
  padding: 0.7rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  min-height: 100px;
  margin-bottom: 1rem;
`;

const SubmitButton = styled.button`
  background-color: #222;
  color: white;
  padding: 0.8rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: background 0.3s;
  &:hover {
    background-color: #000;
  }
`;

const HowItWorksSection = styled.section`
  background-color: #f8f9fa;
  text-align: center;
  padding: 60px 20px;
  width:100%;
  margin-top: 0; /* Ensures no accidental overlap */

  h2 {
    font-size: 2rem;
    margin-bottom: 2rem;
    color: #333;
  }
;
`;
  
const StepsContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 2rem;
    margin-bottom: 3rem;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
    }
    ;
`;
  
const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 200px;
`;

const StepNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #E55B41;
  margin-bottom: 1rem;
`;
  
const StepDescription = styled.p`
  font-size: 1rem;
  color: #666;
`;
  
const DemoVideo = styled.div`
    max-width: 800px;
    margin: 0 auto;
    z-index:1;

    video {
        width: 100%;
        height: 450px;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 768px) {
        video {
        height: 315px;
        }
    }
    ;
`;
  
// Styled Components
const FeaturesContainer = styled.section`
   padding: 4rem 2rem;
  background-color: #f8f9fa; // Light background
  text-align: center;
  width: 100%;

  h2 {
    margin-bottom: 2rem;
    font-size: 2rem;
    color: #333;
  }
;
`;
  
const CardGrid = styled.div`
   display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr; // Stacks cards on smaller screens
  }
;
`;
  
const FeatureCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  text-align: center;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.05);
  }
`;
  
const FeatureImage = styled.img`
  width: 100%;
  height: auto;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  object-fit: cover;
  display: block;
  margin-bottom: 1rem;
`;
  
const FeatureTitle = styled.h3`
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.p`
    font-size: 1rem;
    color: #666;
`;

const HeroVideoSection = styled.section`
  position: relative;
  width: 100%;
  height: 70vh; // Full screen height
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 70px; // Push down from fixed navbar height
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroTextContainer = styled.div`
  color: white;
  position: absolute;
  top:50px;
  left:150px;
  max-width: 600px;
  z-index: 0;

  h1 {
    font-size: 3rem;
    font-weight: bold;
    line-height: 1.3;
    margin-bottom: 1rem;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.6);
  }

  p {
    font-size: 1.3rem;
    margin-bottom: 2rem;
    color: #eee;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
    p {
      font-size: 1rem;
    }
  }
`;
 
// Container for Hero Buttons
const HeroButtonsContainer = styled.div`
    display: flex;
    // justify-content: center;
    gap: 1rem;
    z-index: 2; // Ensures buttons stay above the background

    @media (max-width: 768px) {
      justify-content: center;
      flex-direction: column;
      align-items: center;
    }
    ;
`;